// src/pages/redress/styles.ts
//
// Shared inline-style tokens and tiny helpers for the Redress scheme
// screens. Centralising these keeps the Liability / Calculations /
// Offers / Ledger / Dashboard surfaces visually consistent without
// introducing a new CSS file. All values align with the project's
// existing palette (blue #1976d2, neutral greys, semantic red/green/amber).

import React from 'react';

// --- Palette ---------------------------------------------------------------

export const colors = {
  primary: '#1976d2',
  primaryDark: '#1565c0',
  primaryLight: '#e3f2fd',
  primaryBorder: '#90caf9',

  danger: '#b00020',
  dangerDark: '#8a0018',
  dangerLight: '#ffebee',
  dangerBorder: '#ef9a9a',

  success: '#1b5e20',
  successLight: '#e8f5e9',
  successBorder: '#a5d6a7',

  warning: '#e65100',
  warningLight: '#fff8e1',
  warningBorder: '#ffb74d',

  neutral900: '#111827',
  neutral800: '#1f2937',
  neutral700: '#374151',
  neutral600: '#4b5563',
  neutral500: '#6b7280',
  neutral400: '#9ca3af',
  neutral300: '#d1d5db',
  neutral200: '#e5e7eb',
  neutral100: '#f3f4f6',
  neutral50: '#f9fafb',
  white: '#ffffff',
} as const;

// --- Fieldsets / sections --------------------------------------------------

export const fieldsetStyle: React.CSSProperties = {
  border: `1px solid ${colors.neutral200}`,
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  background: colors.white,
};

export const legendStyle: React.CSSProperties = {
  padding: '0 8px',
  fontWeight: 600,
  fontSize: 13,
  color: colors.neutral700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// --- Inputs ----------------------------------------------------------------

export const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: `1px solid ${colors.neutral300}`,
  borderRadius: 6,
  fontFamily: 'inherit',
  fontSize: 14,
  color: colors.neutral800,
  background: colors.white,
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 34,
};

export const inputNarrowStyle: React.CSSProperties = {
  ...inputStyle,
  width: 90,
};

export const labelStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 8,
  fontSize: 13,
  color: colors.neutral700,
  fontWeight: 500,
};

export const labelInlineStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 0,
  fontSize: 14,
  color: colors.neutral700,
};

// --- Buttons ---------------------------------------------------------------

const buttonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid transparent',
  transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

export const buttonPrimary: React.CSSProperties = {
  ...buttonBase,
  background: colors.primary,
  color: colors.white,
  borderColor: colors.primary,
};

export const buttonSecondary: React.CSSProperties = {
  ...buttonBase,
  background: colors.white,
  color: colors.primary,
  borderColor: colors.primary,
};

export const buttonNeutral: React.CSSProperties = {
  ...buttonBase,
  background: colors.white,
  color: colors.neutral700,
  borderColor: colors.neutral300,
};

export const buttonDanger: React.CSSProperties = {
  ...buttonBase,
  background: colors.danger,
  color: colors.white,
  borderColor: colors.danger,
};

export const buttonDangerOutline: React.CSSProperties = {
  ...buttonBase,
  background: colors.white,
  color: colors.danger,
  borderColor: colors.danger,
};

export const buttonSuccess: React.CSSProperties = {
  ...buttonBase,
  background: colors.success,
  color: colors.white,
  borderColor: colors.success,
};

export const buttonSmall: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  minHeight: 0,
};

// Apply a disabled visual treatment to any button style. The HTML
// ``disabled`` attribute on its own only fires the browser default grey --
// this gives us a consistent, accessible disabled look.
export function applyDisabled(
  base: React.CSSProperties,
  disabled: boolean,
): React.CSSProperties {
  if (!disabled) return base;
  return {
    ...base,
    opacity: 0.55,
    cursor: 'not-allowed',
    boxShadow: 'none',
    filter: 'saturate(0.7)',
  };
}

// --- Tables ----------------------------------------------------------------

export const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: 14,
};

export const thStyle: React.CSSProperties = {
  textAlign: 'left',
  borderBottom: `1px solid ${colors.neutral200}`,
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 600,
  color: colors.neutral600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  background: colors.neutral50,
};

export const tdStyle: React.CSSProperties = {
  borderBottom: `1px solid ${colors.neutral100}`,
  padding: '8px 10px',
  fontSize: 14,
  color: colors.neutral800,
  verticalAlign: 'middle',
};

export const tdNumericStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

// --- Banners ---------------------------------------------------------------

export const bannerInfoStyle: React.CSSProperties = {
  background: colors.primaryLight,
  border: `1px solid ${colors.primaryBorder}`,
  padding: '10px 14px',
  borderRadius: 8,
  marginBottom: 16,
  color: colors.neutral800,
  fontSize: 14,
};

export const bannerWarnStyle: React.CSSProperties = {
  background: colors.warningLight,
  border: `1px solid ${colors.warningBorder}`,
  padding: '10px 14px',
  borderRadius: 8,
  marginBottom: 16,
  color: colors.neutral800,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

export const bannerSuccessStyle: React.CSSProperties = {
  background: colors.successLight,
  border: `1px solid ${colors.successBorder}`,
  padding: '10px 14px',
  borderRadius: 8,
  marginBottom: 12,
  color: colors.success,
  fontSize: 14,
};

export const bannerDangerStyle: React.CSSProperties = {
  background: colors.dangerLight,
  border: `1px solid ${colors.dangerBorder}`,
  padding: '10px 14px',
  borderRadius: 8,
  marginBottom: 12,
  color: colors.danger,
  fontSize: 14,
};

// --- Pills -----------------------------------------------------------------

export const pillBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.6,
};

export const pillDanger: React.CSSProperties = {
  ...pillBase,
  background: colors.dangerLight,
  color: colors.danger,
  border: `1px solid ${colors.dangerBorder}`,
};

export const pillSuccess: React.CSSProperties = {
  ...pillBase,
  background: colors.successLight,
  color: colors.success,
  border: `1px solid ${colors.successBorder}`,
};

export const pillWarning: React.CSSProperties = {
  ...pillBase,
  background: colors.warningLight,
  color: colors.warning,
  border: `1px solid ${colors.warningBorder}`,
};

export const pillNeutral: React.CSSProperties = {
  ...pillBase,
  background: colors.neutral100,
  color: colors.neutral700,
  border: `1px solid ${colors.neutral200}`,
};

// --- Humanise helpers ------------------------------------------------------

export function humaniseStatus(status: string | null | undefined): string {
  // P0-4: coerce defensively — a non-string (undefined caseStatus, or a
  // response OBJECT reaching here) previously threw on `.split` and
  // white-screened the whole admin portal.
  return String(status ?? '')
    .split('_')
    .map((p) => (p.length ? p[0].toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

export function humaniseScheme(scheme: string): string {
  if (scheme === 'scheme_1') return 'Scheme 1';
  if (scheme === 'scheme_2') return 'Scheme 2';
  if (scheme === 'out_of_scope') return 'Out of scope';
  return humaniseStatus(scheme);
}

// Map a status to a pill style. Terminal / closed statuses get a warning
// or neutral tone; active statuses get neutral; paid gets success.
export function pillForStatus(status: string): React.CSSProperties {
  if (status === 'paid') return pillSuccess;
  if (status === 'rejected_fos_referral') return pillDanger;
  if (status.startsWith('closed_')) return pillWarning;
  return pillNeutral;
}
